/**
 * Multi-country configuration for the UEMOA expansion of fodindin.com.
 *
 * Each country shares the same FCFA (XOF) currency, so prices need no
 * conversion. Cities, theme palette, AND background tint differ per country
 * so users can tell at a glance which marketplace they're browsing.
 */

export const COUNTRIES = {
  CI: {
    code: "CI",
    name: "Cote d'Ivoire",
    flag: "🇨🇮",
    capital: "Abidjan",
    cities: [
      // Grandes villes
      "Abidjan", "Bouake", "Yamoussoukro", "San Pedro", "Korhogo",
      "Daloa", "Man", "Gagnoa", "Abengourou", "Bondoukou",
      "Divo", "Soubre", "Odienne",
      // Localités à fort potentiel foncier (axe lagunaire & littoral)
      "Grand-Bassam", "Assinie", "Assouinde", "Mondoukou",
      "Jacqueville", "Dabou", "Bingerville",
      "Ile Boulay", "Ile Vitre",
      "Bregbo", "Eloka-Te", "Asseoufoue", "Motobe",
      "Yaou", "Attinguie", "Samo", "Sassako-Begniny",
    ],
    theme: {
      primary: "#ff7a00",
      primarySoft: "#ffd9b0",
      accent: "#16a34a",
      gradient: "linear-gradient(135deg, #ff7a00 0%, #ffb347 50%, #16a34a 100%)",
      textGradient: "linear-gradient(90deg, #c2410c 0%, #b45309 35%, #4d7c0f 75%, #15803d 100%)",
      background: "#ffffff",                       // ⚪ Blanc pur — branding historique
      bodyGradient: "none",
      stripeColors: ["#ff7a00", "#ffffff", "#16a34a"],
    },
  },
  SN: {
    code: "SN",
    name: "Senegal",
    flag: "🇸🇳",
    capital: "Dakar",
    cities: [
      "Dakar", "Thies", "Saint-Louis", "Kaolack", "Mbour", "Touba",
      "Ziguinchor", "Rufisque", "Diourbel", "Tambacounda", "Louga",
      "Fatick", "Kolda", "Kaffrine", "Sedhiou", "Matam", "Kedougou",
    ],
    theme: {
      primary: "#16a34a",
      primarySoft: "#bbf7d0",
      accent: "#f59e0b",
      gradient: "linear-gradient(135deg, #16a34a 0%, #facc15 50%, #f97316 100%)",
      textGradient: "linear-gradient(90deg, #15803d 0%, #ca8a04 50%, #ea580c 100%)",
      background: "#fef3c7",                       // 🟨 Sable doré (amber-100)
      bodyGradient: "radial-gradient(circle at 100% 0%, #fde68a 0%, #fef9c3 35%, #fffbeb 75%)",
      stripeColors: ["#16a34a", "#facc15", "#dc2626"],
    },
  },
  ML: {
    code: "ML",
    name: "Mali",
    flag: "🇲🇱",
    capital: "Bamako",
    cities: ["Bamako", "Sikasso", "Mopti", "Koutiala", "Segou", "Kayes", "Gao", "Tombouctou", "Kidal"],
    theme: {
      primary: "#16a34a",
      primarySoft: "#bbf7d0",
      accent: "#dc2626",
      gradient: "linear-gradient(135deg, #16a34a 0%, #facc15 50%, #dc2626 100%)",
      textGradient: "linear-gradient(90deg, #15803d 0%, #ca8a04 50%, #b91c1c 100%)",
      background: "#fed7aa",                       // 🟧 Terracotta saharienne (orange-200 light)
      bodyGradient: "radial-gradient(circle at 0% 0%, #fed7aa 0%, #ffedd5 40%, #fff7ed 80%)",
      stripeColors: ["#16a34a", "#facc15", "#dc2626"],
    },
  },
  BF: {
    code: "BF",
    name: "Burkina Faso",
    flag: "🇧🇫",
    capital: "Ouagadougou",
    cities: ["Ouagadougou", "Bobo-Dioulasso", "Koudougou", "Banfora", "Ouahigouya", "Kaya", "Tenkodogo", "Fada N'Gourma"],
    theme: {
      primary: "#dc2626",
      primarySoft: "#fecaca",
      accent: "#16a34a",
      gradient: "linear-gradient(135deg, #dc2626 0%, #facc15 50%, #16a34a 100%)",
      textGradient: "linear-gradient(90deg, #b91c1c 0%, #ca8a04 50%, #15803d 100%)",
      background: "#fecaca",                       // 🟥 Rosé brique (red-200)
      bodyGradient: "radial-gradient(circle at 100% 100%, #fca5a5 0%, #fecaca 35%, #fee2e2 70%, #fef2f2 100%)",
      stripeColors: ["#dc2626", "#16a34a", "#facc15"],
    },
  },
  NE: {
    code: "NE",
    name: "Niger",
    flag: "🇳🇪",
    capital: "Niamey",
    cities: ["Niamey", "Zinder", "Maradi", "Agadez", "Tahoua", "Dosso", "Diffa", "Tillaberi"],
    theme: {
      primary: "#ea580c",
      primarySoft: "#fed7aa",
      accent: "#16a34a",
      gradient: "linear-gradient(135deg, #ea580c 0%, #fde68a 50%, #16a34a 100%)",
      textGradient: "linear-gradient(90deg, #c2410c 0%, #d97706 50%, #15803d 100%)",
      background: "#ffedd5",                       // 🟠 Orange Sahara (orange-100)
      bodyGradient: "linear-gradient(135deg, #fed7aa 0%, #ffedd5 40%, #fff7ed 80%)",
      stripeColors: ["#ea580c", "#ffffff", "#16a34a"],
    },
  },
  BJ: {
    code: "BJ",
    name: "Benin",
    flag: "🇧🇯",
    capital: "Cotonou",
    cities: ["Cotonou", "Porto-Novo", "Parakou", "Abomey", "Bohicon", "Natitingou", "Djougou", "Lokossa"],
    theme: {
      primary: "#16a34a",
      primarySoft: "#bbf7d0",
      accent: "#facc15",
      gradient: "linear-gradient(135deg, #16a34a 0%, #facc15 50%, #dc2626 100%)",
      textGradient: "linear-gradient(90deg, #15803d 0%, #eab308 50%, #b91c1c 100%)",
      background: "#d9f99d",                       // 🟢 Vert tendre (lime-200)
      bodyGradient: "radial-gradient(circle at 0% 100%, #bef264 0%, #d9f99d 30%, #ecfccb 70%, #f7fee7 100%)",
      stripeColors: ["#16a34a", "#facc15", "#dc2626"],
    },
  },
  TG: {
    code: "TG",
    name: "Togo",
    flag: "🇹🇬",
    capital: "Lome",
    cities: ["Lome", "Sokode", "Kara", "Atakpame", "Kpalime", "Tsevie", "Aneho", "Dapaong"],
    theme: {
      primary: "#16a34a",
      primarySoft: "#bbf7d0",
      accent: "#dc2626",
      gradient: "linear-gradient(135deg, #16a34a 0%, #facc15 50%, #dc2626 100%)",
      textGradient: "linear-gradient(90deg, #166534 0%, #d97706 50%, #b91c1c 100%)",
      background: "#a7f3d0",                       // 🟩 Vert menthe (emerald-200)
      bodyGradient: "linear-gradient(135deg, #6ee7b7 0%, #a7f3d0 30%, #d1fae5 65%, #ecfdf5 100%)",
      stripeColors: ["#16a34a", "#facc15", "#dc2626"],
    },
  },
};

// All 7 UEMOA countries now open. Adding a new country = add to COUNTRIES + this list.
export const ACTIVE_COUNTRIES = ["CI", "SN", "ML", "BF", "NE", "BJ", "TG"];

export const DEFAULT_COUNTRY = "CI";

export const getCountry = (code) => COUNTRIES[code] || COUNTRIES[DEFAULT_COUNTRY];

export const isCountryActive = (code) => ACTIVE_COUNTRIES.includes(code);
